import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import SEO, { professionalServiceSchema, faqSchema } from "@/components/SEO";

const ERIKA_PORTRAIT = "/images/dr-erika-tajra.png";
const IMG_CLINIC = "/images/clinic.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] } },
};
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

/* ─── HERO ─── */
function HeroSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 min-h-screen flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-0 items-center w-full pt-28 lg:pt-0">
          <motion.div style={{ y: textY, opacity: textOpacity }} className="relative z-10 lg:pr-16">
            <motion.div variants={stagger} initial="hidden" animate="visible">
              <motion.p variants={fadeUp} className="font-['Cormorant_Garamond'] italic text-lg text-[#D4AF37] mb-3">
                Dr Erika Tajra
              </motion.p>
              <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] text-[#2D2D2D] mb-8">
                Precision Medicine.{" "}<br />
                Personalised Biology.{" "}<br />
                <span className="italic font-normal text-[#D4AF37]">Lifelong Health.</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="text-lg font-light leading-relaxed text-[#6B6B6B] mb-6 max-w-lg">
                I work with individuals who want to understand their body at the
                deepest level — and make decisions that shape their health, longevity,
                and fertility for decades to come.
              </motion.p>
              <motion.div variants={fadeUp} className="mb-10">
                <p className="text-base font-light text-[#2D2D2D] mb-1">This is not standard medicine.</p>
                <p className="text-base font-medium text-[#2D2D2D]">This is clinical intelligence applied to your biology.</p>
              </motion.div>
              <motion.div variants={fadeUp}>
                <Link to="/apply" data-testid="hero-book-btn" className="btn-gold inline-flex items-center gap-2">
                  Apply for a Consultation <ArrowRight size={16} />
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: imageY }} className="relative">
            <div className="relative lg:ml-8">
              <div className="absolute -top-4 -right-4 w-full h-full border border-[#D4AF37]/20 hidden lg:block" />
              <img
                src={ERIKA_PORTRAIT}
                alt="Dr. Erika Tajra"
                data-testid="hero-portrait"
                className="w-full max-w-lg mx-auto lg:max-w-none aspect-[3/4] object-cover object-top relative z-10"
              />
              <div className="absolute -bottom-6 left-8 w-24 h-1 bg-[#D4AF37] hidden lg:block" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── WHAT I DO ─── */
function WhatIDoSection() {
  return (
    <section id="what-i-do" className="py-24 md:py-36 bg-[#F5F2EB]">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">What I Do</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-[#2D2D2D] mb-10 leading-[1.15]">
            I analyse and integrate{" "}<br className="hidden md:block" />
            complex biological data:
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12"
        >
          {["Genetics (DNA)", "Microbiome (gut, vaginal, oral)", "Blood biomarkers", "Clinical history"].map((item, i) => (
            <div key={i} className="flex items-center gap-3 bg-[#FDFBF7] p-5 border border-[rgba(45,45,45,0.06)]">
              <div className="w-2 h-2 bg-[#D4AF37] rounded-full flex-shrink-0" />
              <span className="text-base font-light text-[#2D2D2D]">{item}</span>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.25 }}
        >
          <p className="text-xl font-light text-[#2D2D2D] leading-relaxed mb-6">
            To create a precise, personalised strategy for your health.
          </p>
          <p className="text-base font-light text-[#6B6B6B] mb-1">Not guesswork.</p>
          <p className="text-base font-light text-[#6B6B6B]">Not generic protocols.</p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── WHY THIS MATTERS ─── */
function WhyThisMattersSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  return (
    <section ref={ref} className="py-24 md:py-36 bg-[#FDFBF7] overflow-hidden relative">
      <motion.div style={{ y }} className="absolute top-0 right-0 w-1/3 h-full opacity-[0.03] pointer-events-none hidden lg:block">
        <img src={IMG_CLINIC} alt="" className="w-full h-full object-cover" />
      </motion.div>
      <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">Why This Matters</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-[#2D2D2D] mb-10 leading-[1.15]">
            Most people receive{" "}<br className="hidden md:block" />fragmented care
          </h2>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="space-y-3 mb-10">
          {["One doctor looks at symptoms", "Another looks at labs", "No one connects the system"].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-1.5 h-1.5 bg-[#6B6B6B] rounded-full mt-2.5 flex-shrink-0" />
              <span className="text-base font-light text-[#6B6B6B]">{item}</span>
            </div>
          ))}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
          <p className="text-xl font-medium text-[#2D2D2D] mb-6">I do.</p>
          <p className="text-base font-light text-[#6B6B6B] leading-relaxed mb-1">Because your body is not a list of organs —</p>
          <p className="text-base font-light text-[#6B6B6B] leading-relaxed mb-10">it is a dynamic biological network.</p>
          <p className="text-lg font-light text-[#2D2D2D] mb-8">When you understand that network:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {["Energy stabilises", "Performance improves", "Chronic symptoms resolve", "Disease risk becomes modifiable"].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-[1px] bg-[#D4AF37] flex-shrink-0" />
                <span className="text-base font-light text-[#2D2D2D]">{item}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── WHO THIS IS FOR ─── */
const WHO_LIST = [
  "Individuals who want to optimise their health and performance",
  "Women and men preparing for pregnancy — individually or as a couple",
  "Individuals considering fertility preservation (egg, sperm, or embryo freezing), including those not currently in a relationship",
  "Couples optimising their biology before conception",
  "Women navigating fertility challenges (IVF, miscarriage, unexplained infertility)",
  "Individuals with chronic, unexplained symptoms",
  "Patients focused on longevity and disease prevention",
  "Highly informed individuals seeking deeper, data-driven answers",
];

function WhoThisIsForSection() {
  return (
    <section id="who" className="py-24 md:py-36 bg-[#F5F2EB]">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">Who This Is For</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#2D2D2D] mb-10">I work with:</h2>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="space-y-5 mb-12">
          {WHO_LIST.map((item, i) => (
            <div key={i} className="flex items-start gap-4">
              <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2.5 flex-shrink-0" />
              <span className="text-lg font-light text-[#2D2D2D] leading-relaxed">{item}</span>
            </div>
          ))}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
          <p className="text-base font-light text-[#6B6B6B] leading-relaxed mb-1">
            This is for those who want clarity —
          </p>
          <p className="text-base font-medium text-[#2D2D2D] mb-10">
            and who are ready to take ownership of their biology.
          </p>
          <Link to="/apply" data-testid="who-book-btn" className="btn-gold inline-flex items-center gap-2">
            Apply Now <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── HOW I WORK ─── */
const PROCESS_STEPS = [
  { num: "01", title: "Initial Consultation", desc: "In-depth clinical assessment and pattern identification" },
  { num: "02", title: "Precision Analysis", desc: "Genomics, microbiome, and laboratory data integration" },
  { num: "03", title: "Personalised Strategy", desc: "Targeted nutrition, supplementation, and optimisation" },
  { num: "04", title: "Ongoing Optimisation", desc: "Refinement based on response, data, and goals" },
];

function HowIWorkSection() {
  return (
    <section id="how-i-work" className="py-24 md:py-36 bg-[#FDFBF7]">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">How I Work</p>
        </motion.div>

        <div className="space-y-0">
          {PROCESS_STEPS.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="flex gap-8 py-10 border-t border-[rgba(45,45,45,0.08)]"
            >
              <span className="text-4xl md:text-5xl font-medium text-[#E6DCCA] flex-shrink-0 w-16">{step.num}</span>
              <div>
                <h3 className="text-xl font-medium text-[#2D2D2D] mb-2">{step.title}</h3>
                <p className="text-base font-light leading-relaxed text-[#6B6B6B]">{step.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── PRICING / WORK WITH ME ─── */
function PricingSection() {
  return (
    <section id="pricing" className="py-24 md:py-36 bg-[#1C1C1C]">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="mb-16">
          <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">Work With Me</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {/* Initial Consultation */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="bg-[#FDFBF7] p-8 md:p-10 flex flex-col">
            <h3 className="text-2xl font-medium text-[#2D2D2D] mb-2">Initial Consultation</h3>
            <p className="text-3xl font-medium text-[#D4AF37] mb-6">$400</p>
            <p className="text-sm font-light leading-relaxed text-[#6B6B6B] flex-1">
              A comprehensive 1:1 session to understand your health and define next steps.
            </p>
          </motion.div>

          {/* Personalised Longevity & Wellness Programme */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="bg-[#FDFBF7] p-8 md:p-10 flex flex-col relative border-2 border-[#D4AF37]">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#D4AF37] text-white text-xs font-medium tracking-wide uppercase px-4 py-1">
              Signature
            </div>
            <h3 className="text-xl font-medium text-[#2D2D2D] mb-2 leading-tight">Personalised Longevity &amp; Wellness Programme</h3>
            <p className="text-3xl font-medium text-[#D4AF37] mb-6">$3,500 – $5,000</p>
            <p className="text-sm font-light leading-relaxed text-[#6B6B6B] mb-4">
              A complete, integrated analysis including:
            </p>
            <div className="space-y-2 flex-1">
              {[
                "Genetics interpretation",
                "Personalised nutrition and supplement strategy",
                "Microbiome analysis",
                "Clinical and pharmacogenomic integration",
                "Personalised protocol",
                "Follow-up session",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <div className="w-1 h-1 bg-[#D4AF37] rounded-full" />
                  <span className="text-xs text-[#2D2D2D]">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Follow-Up */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="bg-[#FDFBF7] p-8 md:p-10 flex flex-col">
            <h3 className="text-2xl font-medium text-[#2D2D2D] mb-2">Follow-Up Consultation</h3>
            <p className="text-3xl font-medium text-[#D4AF37] mb-6">$250</p>
            <p className="text-sm font-light leading-relaxed text-[#6B6B6B] flex-1">
              Ongoing support and optimisation.
            </p>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 }} className="text-center">
          <Link to="/apply" data-testid="pricing-apply-btn" className="btn-gold inline-flex items-center gap-2 text-base">
            Apply to Work With Me <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── MY APPROACH ─── */
function MyApproachSection() {
  return (
    <section className="py-24 md:py-36 bg-[#F5F2EB]">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">My Approach</p>
          <p className="text-lg font-light text-[#2D2D2D] leading-relaxed mb-8">
            I guide you in selecting the most appropriate testing when needed.
          </p>
          <p className="text-lg font-light text-[#6B6B6B] leading-relaxed mb-10">
            I work with data from any laboratory.
          </p>
          <p className="text-base font-light text-[#6B6B6B] mb-6">My role is to:</p>
          <div className="space-y-4 mb-10">
            {["Interpret", "Integrate", "Translate into action"].map((item) => (
              <div key={item} className="flex items-center gap-3">
                <ArrowRight size={16} className="text-[#D4AF37] flex-shrink-0" />
                <span className="text-xl font-medium text-[#2D2D2D]">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-base font-light text-[#6B6B6B] leading-relaxed mb-2">
            Because data alone does not change health.
          </p>
          <p className="text-lg font-medium text-[#2D2D2D]">Understanding does.</p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── WHAT MAKES THIS DIFFERENT ─── */
function WhatMakesDifferentSection() {
  return (
    <section className="py-24 md:py-36 bg-[#FDFBF7]">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">What Makes This Different</p>
          <div className="space-y-5 mb-12">
            {[
              "UK-trained medical doctor",
              "Advanced training in genomics and personalised medicine",
              "Integration of functional and clinical medicine",
              "Focus on longevity, fertility, and disease prevention",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2.5 flex-shrink-0" />
                <span className="text-lg font-light text-[#2D2D2D] leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-lg font-light text-[#2D2D2D] leading-relaxed mb-1">
            I look at the whole system —
          </p>
          <p className="text-lg font-light text-[#2D2D2D] leading-relaxed">
            including biology, mind, and deeper health patterns.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── FINAL NOTE ─── */
function FinalNoteSection() {
  return (
    <section className="py-24 md:py-36 bg-[#F5F2EB]">
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
          <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-8">A Final Note</p>
          <p className="text-xl md:text-2xl font-light text-[#2D2D2D] leading-relaxed mb-3">Your body is not random.</p>
          <p className="text-lg font-light text-[#6B6B6B] leading-relaxed mb-1">Symptoms are signals.</p>
          <p className="text-lg font-light text-[#6B6B6B] leading-relaxed mb-1">Patterns can be understood.</p>
          <p className="text-lg font-light text-[#6B6B6B] leading-relaxed mb-8">Outcomes can be changed.</p>
          <p className="text-lg font-light text-[#2D2D2D] leading-relaxed mb-2">When biology is understood at depth,</p>
          <p className="text-xl font-medium text-[#2D2D2D] mb-10">health becomes something you can actively shape.</p>
          <Link to="/apply" data-testid="final-book-btn" className="btn-gold inline-flex items-center gap-2">
            Apply for a Consultation <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── BOOK CTA + SIGNATURE ─── */
function SignatureSection() {
  return (
    <section className="py-16 md:py-24 bg-[#1C1C1C]">
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
        <p className="font-['Cormorant_Garamond'] italic text-2xl md:text-3xl text-[#FDFBF7]/50">
          "I don't treat symptoms. I decode biology."
        </p>
      </div>
    </section>
  );
}

/* ─── WORKING WITH YOUR MEDICAL TEAM ─── */
function MedicalTeamSection() {
  return (
    <section className="py-24 md:py-36 bg-[#FDFBF7]">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <p className="text-sm font-medium tracking-widest uppercase text-[#D4AF37] mb-4">Working With Your Medical Team</p>
          <p className="text-xl md:text-2xl font-light text-[#2D2D2D] leading-relaxed mb-6">
            I support you in communicating your results — including genetic, microbiome,
            and pharmacogenomic data — with your existing doctors.
          </p>
          <p className="text-lg font-light text-[#6B6B6B] leading-relaxed">
            Wherever you are in the world, I help implement personalised medicine into your care.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── MAIN PAGE ─── */
export default function HomePage() {
  const homeFaq = [
    {
      question: "What is nutrigenomics?",
      answer: "Nutrigenomics is the study of how your genes interact with the nutrients you consume. It allows us to create truly personalised nutrition and supplement strategies based on your unique genetic profile.",
    },
    {
      question: "Who is Dr. Erika Tajra?",
      answer: "Dr. Erika Tajra is a UK-trained medical doctor specialising in personalised medicine, nutrigenomics, microbiome science, and longevity protocols. She holds a Master's in Personalised Medicine from UCL.",
    },
    {
      question: "What services does Dr. Erika offer?",
      answer: "Dr. Erika offers Initial Consultations ($400), the Personalised Longevity & Wellness Programme ($3,500-$5,000), and Follow-Up Consultations ($250). All services are available in person in London or virtually worldwide.",
    },
    {
      question: "Is Dr. Erika a licensed physician in the United States?",
      answer: "No. Dr. Erika holds UK and international medical qualifications. She is not a licensed physician in the United States and does not diagnose, treat, or prescribe. Her services are health and wellness consultations for educational and informational purposes.",
    },
    {
      question: "Can I book a consultation online?",
      answer: "Consultations are by application. Submit a short application through the website sharing what you're looking for, and Dr. Erika will review and respond within 48 hours. Sessions are available in person in London or virtually worldwide.",
    },
  ];

  return (
    <main data-testid="home-page">
      <SEO
        path="/"
        description="Dr. Erika Tajra — UK-trained medical doctor specialising in nutrigenomics, microbiome science, and personalised longevity protocols. Apply for a consultation for genomics-driven health optimisation."
        jsonLd={[professionalServiceSchema(), faqSchema(homeFaq)]}
      />
      <HeroSection />
      <WhatIDoSection />
      <WhyThisMattersSection />
      <WhoThisIsForSection />
      <HowIWorkSection />
      <PricingSection />
      <MyApproachSection />
      <WhatMakesDifferentSection />
      <FinalNoteSection />
      <SignatureSection />
      <MedicalTeamSection />
    </main>
  );
}
